import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useAgent } from './AgentContext';
import {
  getFirestore,
  collection,
  onSnapshot,
  doc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const IntegrationsContext = createContext({});

export const IntegrationsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentAgent } = useAgent();
  const db = getFirestore();
  const functions = getFunctions();

  const [platforms, setPlatforms] = useState([]); // full list of Platform docs
  const [authenticators, setAuthenticators] = useState([]); // joined list
  const [integrationRequests, setIntegrationRequests] = useState([]); // integration requests

  // Listen to platform catalog (collection "platforms") once per app load
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'platforms'), snap => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPlatforms(rows);
    });
    return unsub;
  }, [db]);

  // Listen to current agent's authenticators sub-collection
  useEffect(() => {
    if (!currentUser || !currentAgent?.id) {
      setAuthenticators([]);
      return undefined;
    }
    const col = collection(db, 'agents', currentAgent.id, 'authenticators');
    const unsub = onSnapshot(col, snap => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // enrich with platform meta
      const joined = rows.map(a => {
        const platform = platforms.find(p => p.id === a.platformId);
        return { ...a, platform };
      });
      setAuthenticators(joined);
    });
    return unsub;
  }, [db, currentUser, currentAgent, platforms]);

  // Listen to current agent's integration requests sub-collection
  useEffect(() => {
    if (!currentUser || !currentAgent?.id) {
      console.log('No user or agent, clearing integration requests');
      setIntegrationRequests([]);
      return undefined;
    }
    console.log('🔔 Setting up integration requests listener for agent:', currentAgent.id);
    console.log('🔔 Firestore path:', `agents/${currentAgent.id}/integrationRequests`);
    const col = collection(db, 'agents', currentAgent.id, 'integrationRequests');
    const unsub = onSnapshot(
      col, 
      (snap) => {
        console.log('✅ Integration requests snapshot received:', snap.docs.length, 'requests');
        const rows = snap.docs.map(d => {
          const data = d.data();
          console.log('📄 Integration request data:', { id: d.id, ...data });
          return { 
            id: d.id, 
            ...data,
            // Convert Firestore timestamps to dates if needed
            requestedAt: data.requestedAt?.toDate ? data.requestedAt.toDate() : data.requestedAt,
            connectedAt: data.connectedAt?.toDate ? data.connectedAt.toDate() : data.connectedAt,
          };
        });
        console.log('🔍 Available platforms:', platforms.map(p => p.id));
        // enrich with platform meta
        const joined = rows.map(r => {
          const platform = platforms.find(p => p.id === r.platformId);
          console.log('🔗 Enriching request with platform:', r.platformId, '→', platform?.name || 'NOT FOUND');
          return { ...r, platform };
        });
        console.log('💾 Final integration requests to set:', joined);
        setIntegrationRequests(joined);
      },
      (error) => {
        console.error('❌ Error in integration requests listener:', error);
      }
    );
    return unsub;
  }, [db, currentUser, currentAgent, platforms]);

  const connect = useCallback(async (platformId, params) => {
    const callable = httpsCallable(functions, 'authenticators-connect');
    const result = await callable({ platform: platformId, ...params });
    return result.data; // Return the created authenticator info
  }, [functions]);

  const disconnect = useCallback(async authenticatorId => {
    if (!currentUser || !currentAgent?.id) return;
    const callable = httpsCallable(functions, 'authenticators-disconnect');
    await callable({ authenticatorId, agentId: currentAgent.id });
  }, [currentUser, currentAgent, functions]);

  const requestIntegration = useCallback(async (platformId) => {
    if (!currentUser || !currentAgent?.id) {
      throw new Error('User or agent not available');
    }
    const callable = httpsCallable(functions, 'agents-requestIntegrationCallable');
    const result = await callable({ agentId: currentAgent.id, platformId });
    return result.data; // Return the created integration request info
  }, [currentUser, currentAgent, functions]);

  const value = {
    platforms,
    authenticators,
    integrationRequests,
    connect,
    disconnect,
    requestIntegration,
  };
  return (
    <IntegrationsContext.Provider value={value}>{children}</IntegrationsContext.Provider>
  );
};

export const useIntegrations = () => useContext(IntegrationsContext);
